import {
  RtkAudioVisualizer,
  RtkAvatar,
  RtkNameTag,
  RtkParticipantTile,
  RtkParticipantsAudio,
  RtkSimpleGrid,
  provideRtkDesignSystem,
} from '@cloudflare/realtimekit-react-ui';
import { useRealtimeKitMeeting, useRealtimeKitSelector } from '@cloudflare/realtimekit-react';
import clsx from 'clsx';
import { useEffect, useCallback } from 'react';
import Draggable from 'react-draggable';
import { Mic, Video, X } from 'react-feather';

function Grid() {
  const { meeting } = useRealtimeKitMeeting();
  const participants = useRealtimeKitSelector(m => m.participants.active.toArray());

  return (
    <div className="relative flex place-items-center justify-center flex-1 overflow-hidden m-4">
      {participants.length === 0 && <p className="text-2xl">People haven&apos;t joined yet.</p>}
      {participants.length > 0 && (
        <RtkSimpleGrid participants={participants} meeting={meeting} aspectRatio="1:1" gap={18} />
      )}
    </div>
  );
}

function Controlbar() {
  const { meeting } = useRealtimeKitMeeting();

  const { videoEnabled, audioEnabled } = useRealtimeKitSelector(m => ({
    videoEnabled: m.self.videoEnabled,
    audioEnabled: m.self.audioEnabled,
  }));

  const toggleCamera = useCallback(() => {
    if (meeting.self.videoEnabled) {
      meeting.self.disableVideo();
    } else {
      meeting.self.enableVideo();
    }
  }, [meeting]);

  const toggleMic = useCallback(() => {
    if (meeting.self.audioEnabled) {
      meeting.self.disableAudio();
    } else {
      meeting.self.enableAudio();
    }
  }, [meeting]);

  const leaveMeeting = useCallback(() => {
    meeting.leaveRoom();
    window.location.href = '/meet-me';
  }, [meeting]);

  return (
    <div className="z-20 flex w-full max-w-xs sm:w-min pt-4" style={{marginLeft: '30%'}}>
      <div className="w-full p-4 gap-4 bg-neutral-800 shadow-2xl shadow-black rounded-2xl md:rounded-3xl flex flex-col">
        <div className="flex items-center justify-evenly gap-8">
          <button
            className={clsx(
              'p-3 flex items-center justify-center rounded-full',
              audioEnabled ? 'bg-white text-black' : 'bg-neutral-700 text-white'
            )}
            onClick={toggleMic}
          >
            <Mic />
          </button>
          <button
            className={clsx(
              'p-3 flex items-center justify-center rounded-full',
              videoEnabled ? 'bg-white text-black' : 'bg-neutral-700 text-white'
            )}
            onClick={toggleCamera}
          >
            <Video />
          </button>
          <button
            className="bg-red-500 text-white p-3 flex items-center justify-center rounded-full"
            onClick={leaveMeeting}
          >
            <X />
          </button>
        </div>
      </div>
    </div>
  );
}

export default function MeetingRoom() {
  const { meeting } = useRealtimeKitMeeting();

  useEffect(() => {
    provideRtkDesignSystem(document.body, {
      colors: {
        'video-bg': '#333333',
      },
    });
  }, []);

  return (
    <div className="flex align-middle items-center justify-center m-4">
      <div className="relative bg-black text-white overflow-hidden rounded-3xl shadow-2xl">
        <div className="p-4 flex flex-col" style={{height: '80vh', width: '30vw'}}>
          <RtkParticipantsAudio meeting={meeting} />
          <Grid />
          <Draggable bounds="parent">
            <RtkParticipantTile
              participant={meeting.self}
              meeting={meeting}
              key={meeting.self.id}
              className="z-10 absolute bottom-44 right-4 shadow-black shadow-2xl aspect-square w-32 h-auto cursor-move duration-0"
            >
              <RtkAvatar participant={meeting.self} size="sm" />
              <RtkNameTag participant={meeting.self} size="sm">
                <RtkAudioVisualizer participant={meeting.self} size="sm" slot="start" />
              </RtkNameTag>
            </RtkParticipantTile>
          </Draggable>
          <Controlbar />
        </div>
      </div>
    </div>
  );
}
